// [목적] folio-crud REST API 클라이언트 — fetchWithEnvelope + zod parse 까지 일괄 처리
// [이유] 컴포넌트는 client 함수만 호출, envelope/zod 검증은 본 모듈이 책임
// [사이드 이팩트] 응답 zod parse 실패 시 ZodError throw → ErrorState 분기. mock(MSW) 응답도 동일 검증
// [수정 시 고려사항] BE 응답 구조 변경 시 packages/types/folio.ts 의 zod 스키마와 동시 갱신

import {
  dayPlanSchema,
  folioListSchema,
  folioSchema,
  planItemSchema,
  type CreateDayInput,
  type CreateFolioInput,
  type CreateItemInput,
  type DayPlan,
  type Folio,
  type FolioList,
  type ForkFolioInput,
  type ListFoliosQuery,
  type PlanItem,
  type ReorderItemsInput,
  type UpdateDayInput,
  type UpdateFolioInput,
  type UpdateItemInput,
} from '@gangwoncanvas/types';
import { z } from 'zod';

import { fetchWithEnvelope } from './http.js';

function buildQuery(params: Partial<ListFoliosQuery>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null) continue;
    usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : '';
}

export async function getFolioById(id: string): Promise<Folio> {
  const data = await fetchWithEnvelope<unknown>(`/folios/${id}`);
  return folioSchema.parse(data);
}

export async function listFolios(params: Partial<ListFoliosQuery> = {}): Promise<FolioList> {
  const data = await fetchWithEnvelope<unknown>(`/folios${buildQuery(params)}`);
  return folioListSchema.parse(data);
}

export async function createFolio(input: CreateFolioInput): Promise<Folio> {
  const data = await fetchWithEnvelope<unknown>(`/folios`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return folioSchema.parse(data);
}

export async function patchFolio(id: string, input: UpdateFolioInput): Promise<Folio> {
  const data = await fetchWithEnvelope<unknown>(`/folios/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
  return folioSchema.parse(data);
}

export async function deleteFolio(id: string): Promise<void> {
  await fetchWithEnvelope<void>(`/folios/${id}`, { method: 'DELETE' });
}

export async function reorderItems(folioId: string, input: ReorderItemsInput): Promise<PlanItem[]> {
  const data = await fetchWithEnvelope<unknown>(`/folios/${folioId}/items/reorder`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return z.array(planItemSchema).parse(data);
}

// [목적] PlanItem 생성 — POST /folios/:id/items
// [이유] WC-FE-FOLIO-03 usePlanItems hook 의 create mutation 에서 호출
// [사이드 이팩트] 응답을 planItemSchema 로 parse — BE 필드 불일치 시 ZodError throw
// [수정 시 고려사항] input 에 dayPlanId 필수 (createItemSchema 참고). BE가 생성된 PlanItem 전체 반환
export async function createPlanItem(folioId: string, input: CreateItemInput): Promise<PlanItem> {
  const data = await fetchWithEnvelope<unknown>(`/folios/${folioId}/items`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return planItemSchema.parse(data);
}

// [목적] PlanItem 부분 수정 — PATCH /folios/:id/items/:itemId
// [이유] WC-FE-FOLIO-03 usePlanItems hook 의 update mutation 에서 호출
// [사이드 이팩트] 응답을 planItemSchema 로 parse
// [수정 시 고려사항] UpdateItemInput 은 partial — kind 변경 시 poiId nullable 검증 (updateItemSchema refine)
export async function patchPlanItem(
  folioId: string,
  itemId: string,
  input: UpdateItemInput,
): Promise<PlanItem> {
  const data = await fetchWithEnvelope<unknown>(`/folios/${folioId}/items/${itemId}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
  return planItemSchema.parse(data);
}

// [목적] PlanItem 삭제 — DELETE /folios/:id/items/:itemId
// [이유] WC-FE-FOLIO-03 usePlanItems hook 의 delete mutation 에서 호출
// [사이드 이팩트] 204 No Content 반환 → fetchWithEnvelope 가 undefined 반환, void 처리
// [수정 시 고려사항] 삭제 후 queryClient.invalidate 로 folio 캐시 무효화 필요 (hook 책임)
export async function deletePlanItem(folioId: string, itemId: string): Promise<void> {
  await fetchWithEnvelope<void>(`/folios/${folioId}/items/${itemId}`, { method: 'DELETE' });
}

// [목적] DayPlan 생성 — POST /folios/:id/days
// [이유] WC-FE-FOLIO-02 useDayPlans hook 의 create mutation 에서 호출
// [사이드 이팩트] 응답을 dayPlanSchema 로 parse — items 배열은 빈 배열로 반환됨
// [수정 시 고려사항] BE가 dayIndex 를 자동 부여하거나 input 에서 받을 수 있음 (createDaySchema 참고)
export async function createDayPlan(folioId: string, input: CreateDayInput): Promise<DayPlan> {
  const data = await fetchWithEnvelope<unknown>(`/folios/${folioId}/days`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return dayPlanSchema.parse(data);
}

// [목적] DayPlan 부분 수정 — PATCH /folios/:id/days/:dayId
// [이유] WC-FE-FOLIO-02 useDayPlans hook 의 update mutation 에서 호출
// [사이드 이팩트] 응답을 dayPlanSchema 로 parse (items 배열 포함)
// [수정 시 고려사항] totalCostEstimate 는 BE 가 재계산해서 반환 — FE 에서 직접 계산하면 불일치 가능성
export async function patchDayPlan(
  folioId: string,
  dayId: string,
  input: UpdateDayInput,
): Promise<DayPlan> {
  const data = await fetchWithEnvelope<unknown>(`/folios/${folioId}/days/${dayId}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
  return dayPlanSchema.parse(data);
}

// [목적] DayPlan 삭제 — DELETE /folios/:id/days/:dayId
// [이유] WC-FE-FOLIO-02 useDayPlans hook 의 delete mutation 에서 호출
// [사이드 이팩트] 204 No Content 반환. 해당 Day 의 모든 PlanItem 도 CASCADE 삭제됨 (BE DB 정책)
// [수정 시 고려사항] 삭제 후 folio 캐시 invalidate 필수. 낙관적 업데이트 시 dayIndex 재정렬 주의
export async function deleteDayPlan(folioId: string, dayId: string): Promise<void> {
  await fetchWithEnvelope<void>(`/folios/${folioId}/days/${dayId}`, { method: 'DELETE' });
}

// [목적] 폴리오 Fork — POST /folios/:id/fork
// [이유] WC-FE-FOLIO-05 Fork CTA 버튼 클릭 시 호출. 로그인 사용자만 진입 (guest redirect 는 컴포넌트 책임)
// [사이드 이팩트] BE 가 새 Folio 를 생성하여 반환 — folioSchema 로 parse. 응답 ID 로 /folio/:id 로 navigate 권장
// [수정 시 고려사항] forkFolioSchema 는 kind='full'|'day_import' — 현재 CTA 는 kind='full' 고정.
//                   day_import 지원은 후속 folio-day-import Spec 에서 확장
export async function forkFolio(folioId: string, input?: ForkFolioInput): Promise<Folio> {
  const body: ForkFolioInput = input ?? { kind: 'full' };
  const data = await fetchWithEnvelope<unknown>(`/folios/${folioId}/fork`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return folioSchema.parse(data);
}
